import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { costTypeOptions } from "../../../../components/claim/costs/costFieldSchemas.js";
import { useClaimCostTypes } from "../../../../hooks/useClaimCostTypes.js";

/**
 * The cost types the form's cost editor of a Sparte offers by default: the
 * editor inside the step of the Sparte's coverage type, else the editor
 * outside the Sparte steps. Null when that editor declares no list.
 */
export function editorDefaultCostTypes(definition, coverageType) {
  let found = null;

  const walk = (node, stepCoverageType) => {
    if (found || !node || typeof node !== "object") {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((child) => walk(child, stepCoverageType));
      return;
    }
    if (node.control === "claim_costs_editor") {
      if ((stepCoverageType ?? null) === (coverageType ?? null)) {
        found = { cost_types: node.cost_types ?? null };
      }
      return;
    }
    const scope =
      !node.control && typeof node.coverage_type === "string"
        ? node.coverage_type
        : stepCoverageType;
    Object.values(node).forEach((child) => walk(child, scope));
  };

  walk(definition?.formdefinition, null);

  return found?.cost_types ?? null;
}

/**
 * The cost types the cost editors of a Sparte offer: the form's default (null)
 * or an own selection. With allowUnchanged, undefined leaves each Sparte's
 * setting as it is. With mixedCoverage the Sparten sit in steps of different
 * coverage types, so there is no single form default to show. The parent keeps
 * the selection non-empty before saving.
 */
export default function ClaimFormSparteCostTypes({
  sparte,
  definition,
  disabled,
  allowUnchanged = false,
  mixedCoverage = false,
  onChange,
}) {
  const { config, isError, reload } = useClaimCostTypes();
  const allTypes = costTypeOptions(config);
  const ownSelection = sparte.cost_types ?? null;
  const mode =
    allowUnchanged && sparte.cost_types === undefined
      ? "unchanged"
      : ownSelection
        ? "custom"
        : "default";
  const formTypes = mixedCoverage
    ? null
    : editorDefaultCostTypes(definition, sparte.coverage_type);
  const defaultTypes = formTypes ?? allTypes.map(({ key }) => key);
  const formTypesSummary = mixedCoverage
    ? "Je nach Deckungsart der Sparte"
    : formTypes
      ? `Laut Formular: ${allTypes
          .filter(({ key }) => formTypes.includes(key))
          .map(({ label }) => label)
          .join(", ")}`
      : "Nicht im Formular hinterlegt – alle Kostenarten";

  const handleModeChange = (event) => {
    const modeValues = {
      unchanged: undefined,
      default: null,
      custom: defaultTypes,
    };
    onChange(modeValues[event.target.value]);
  };

  const handleToggle = (key) => {
    const next = ownSelection.includes(key)
      ? ownSelection.filter((type) => type !== key)
      : allTypes
          .map((option) => option.key)
          .filter((type) => type === key || ownSelection.includes(type));
    onChange(next);
  };

  if (isError) {
    return (
      <Button size="small" onClick={reload}>
        Kostenarten konnten nicht geladen werden. Erneut versuchen
      </Button>
    );
  }

  if (!config) {
    return (
      <CircularProgress size={20} aria-label="Kostenarten werden geladen" />
    );
  }

  return (
    <Box>
      <RadioGroup
        row
        aria-label={`Kostenarten ${sparte.name}`}
        value={mode}
        onChange={handleModeChange}
      >
        {allowUnchanged && (
          <FormControlLabel
            value="unchanged"
            control={<Radio size="small" disabled={disabled} />}
            label="Unverändert lassen"
          />
        )}
        <FormControlLabel
          value="default"
          control={<Radio size="small" disabled={disabled} />}
          label="Standard des Formulars"
        />
        <FormControlLabel
          value="custom"
          control={<Radio size="small" disabled={disabled} />}
          label="Eigene Auswahl"
        />
      </RadioGroup>
      {mode === "default" && (
        <Typography variant="body2" color="text.secondary">
          {formTypesSummary}
        </Typography>
      )}
      {ownSelection && (
        <FormGroup sx={{ display: "block", columnWidth: 220 }}>
          {allTypes.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              sx={{ display: "flex", breakInside: "avoid" }}
              label={label}
              slotProps={{ typography: { variant: "body2" } }}
              control={
                <Checkbox
                  size="small"
                  checked={ownSelection.includes(key)}
                  disabled={disabled}
                  onChange={() => handleToggle(key)}
                />
              }
            />
          ))}
        </FormGroup>
      )}
    </Box>
  );
}

ClaimFormSparteCostTypes.propTypes = {
  sparte: PropTypes.shape({
    name: PropTypes.string.isRequired,
    coverage_type: PropTypes.string,
    cost_types: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  definition: PropTypes.object,
  disabled: PropTypes.bool,
  allowUnchanged: PropTypes.bool,
  mixedCoverage: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};
